import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import { usePermission } from "../../hooks/usePermission";

function driverLabel(driver) {
  if (!driver) return "Unassigned";
  return `${driver.first_name || ""} ${driver.last_name || ""}`.trim() || "Unassigned";
}

export default function DriverBehaviorPanel({ driverId = null, showDriver = true }) {
  const { hasPermission } = usePermission();
  const canView = hasPermission("/api/driver/safety", "GET");
  const canMarkSeen = hasPermission("/api/driver/safety", "PATCH");
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError("");
    try {
      const params = driverId ? { driverId } : undefined;
      const response = await axios.get(`${config.baseURL}/driver-safety`, { params });
      setEvents(response.data?.events || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not load driver behavior events");
    } finally {
      setLoading(false);
    }
  }, [canView, driverId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  if (!canView) return null;

  const openPhoto = async (eventId) => {
    setError("");
    try {
      const response = await axios.get(`${config.baseURL}/driver-safety/${eventId}/url`);
      setPhotoUrl(response.data?.url || "");
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not open the photo");
    }
  };

  const markSeen = async (eventId) => {
    setError("");
    try {
      await axios.patch(`${config.baseURL}/driver-safety/${eventId}/seen`);
      await loadEvents();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not mark the event seen");
    }
  };

  return (
    <div style={{ marginTop: "16px" }}>
      <h2 style={{ margin: "0 0 8px", fontSize: "18px" }}>Behavior</h2>
      {error ? <p>{error}</p> : null}
      {loading ? <p>Loading events...</p> : null}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "8px" }}>Time</th>
            <th style={{ textAlign: "left", padding: "8px" }}>Vehicle</th>
            {showDriver ? <th style={{ textAlign: "left", padding: "8px" }}>Driver</th> : null}
            <th style={{ textAlign: "left", padding: "8px" }}>Behavior</th>
            <th style={{ textAlign: "left", padding: "8px" }}>Photo</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id}>
              <td style={{ padding: "8px", borderTop: "1px solid #d7dce3" }}>
                {new Date(event.capturedAt).toLocaleString()}
              </td>
              <td style={{ padding: "8px", borderTop: "1px solid #d7dce3" }}>
                {event.vehicle?.VehicleNumber || event.vehicle?.name || "Unlinked"}
              </td>
              {showDriver ? (
                <td style={{ padding: "8px", borderTop: "1px solid #d7dce3" }}>
                  {driverLabel(event.driver)}
                </td>
              ) : null}
              <td style={{ padding: "8px", borderTop: "1px solid #d7dce3" }}>
                {Array.isArray(event.behaviors) && event.behaviors.length
                  ? event.behaviors.join(", ")
                  : "Snapshot only"}
              </td>
              <td style={{ padding: "8px", borderTop: "1px solid #d7dce3" }}>
                <button type="button" onClick={() => openPhoto(event.id)}>
                  Open photo
                </button>
                {canMarkSeen && event.alertId ? (
                  <button type="button" onClick={() => markSeen(event.id)}>
                    Mark seen
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && events.length === 0 ? <p>No driver behavior photos yet.</p> : null}
      {photoUrl ? (
        <img alt="Cabin camera" src={photoUrl} style={{ marginTop: "16px", maxWidth: "480px" }} />
      ) : null}
    </div>
  );
}
